import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { fetchSetupStatus, login, setupPlatformAdmin } from "@/api/auth";
import { Button, Input } from "@/components";
import { useAuthStore } from "@/state/auth.store";
import { useLang } from "@/i18n/LanguageContext";

const EMAIL_KEY = "obf_login_email";
const SLUG_KEY = "obf_login_slug";

export function LoginPage() {
  const { t } = useLang();
  const setupStatus = useQuery({ queryKey: ["setup-status"], queryFn: ({ signal }) => fetchSetupStatus(signal) });

  if (setupStatus.isLoading) return <div className="center-col">{t("Laden…", "Loading…")}</div>;
  return setupStatus.data ? <SetupForm /> : <LoginForm />;
}

function SetupForm() {
  const { t } = useLang();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await setupPlatformAdmin(email, password, fullName);
      setSession(res.token, res.user);
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Aanmaken mislukt", "Could not create account"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center-col">
      <p className="eyebrow">{t("Eerste keer opstarten", "First-time setup")}</p>
      <h1 style={{ marginBottom: 8 }}>{t("Maak de platform-beheerder aan", "Create the platform administrator")}</h1>
      <p style={{ color: "var(--grey-text)", marginBottom: 24 }}>{t("Dit account beheert de tenants (klantbedrijven). Eenmalig.", "This account manages tenants (client companies). One-time.")}</p>
      <form onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        <Input label={t("Naam", "Name")} name="name" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <Input label={t("E-mail", "Email")} name="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input label={t("Wachtwoord", "Password")} name="password" type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" disabled={busy}>{busy ? t("Bezig…", "Working…") : t("Platform-beheerder aanmaken", "Create platform administrator")}</Button>
      </form>
    </div>
  );
}

function LoginForm() {
  const { t } = useLang();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [tenantSlug, setTenantSlug] = useState(() => localStorage.getItem(SLUG_KEY) ?? "");
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY) ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await login(email, password, tenantSlug.trim() || undefined);
      localStorage.setItem(EMAIL_KEY, email);
      localStorage.setItem(SLUG_KEY, tenantSlug.trim());
      setSession(res.token, res.user);
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Inloggen mislukt", "Login failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center-col">
      <p className="eyebrow">{t("Inloggen", "Log in")}</p>
      <h1 style={{ marginBottom: 24 }}>{t("Welkom terug", "Welcome back")}</h1>
      <form onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        <Input
          label={t("Organisatie (slug)", "Organisation (slug)")}
          name="organization"
          autoComplete="organization"
          placeholder={t("bijv. acme — leeg laten voor platform-beheer", "e.g. acme — leave empty for platform admin")}
          value={tenantSlug}
          onChange={(e) => setTenantSlug(e.target.value)}
        />
        <Input label={t("E-mail", "Email")} name="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input label={t("Wachtwoord", "Password")} name="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" disabled={busy}>{busy ? t("Bezig…", "Working…") : t("Inloggen", "Log in")}</Button>
      </form>
      <p style={{ marginTop: 18, display: "flex", gap: 16, flexWrap: "wrap" }}>
        <Link to="/forgot-password" className="eyebrow">{t("Wachtwoord vergeten?", "Forgot password?")}</Link>
        <Link to="/signup" className="eyebrow">{t("Account aanmaken", "Create an account")}</Link>
      </p>
    </div>
  );
}
